import { Box, Paper, Typography, Skeleton, LinearProgress } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Devices as DevicesIcon,
  CheckCircle as OnlineIcon,
  Cancel as OfflineIcon,
  Notifications as AlertIcon,
} from '@mui/icons-material';
import { GlobalStatsRowProps, StatFilter } from '../types';
import { getCardBorder } from '../types';

interface StatCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: string;
  percentage?: number;
  isActive?: boolean;
  onClick?: () => void;
}

function StatCard({ title, value, icon, color, percentage, isActive, onClick }: StatCardProps) {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      }}
      sx={{
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 100,
        borderRadius: 2,
        border: isActive ? `2px solid ${color}` : getCardBorder(theme),
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        backgroundColor: isActive ? alpha(color, 0.04) : undefined,
        '&:hover': {
          boxShadow: theme.shadows[4],
          borderColor: alpha(color, 0.5),
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: alpha(color, 0.1),
            color: color,
            mr: 1.5,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {title}
          </Typography>
          <Typography variant="h5" fontWeight="bold">
            {value}
          </Typography>
        </Box>
      </Box>

      {percentage !== undefined && (
        <Box sx={{ mt: 'auto' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {percentage.toFixed(0)}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={percentage}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: alpha(color, 0.15),
              '& .MuiLinearProgress-bar': {
                backgroundColor: color,
                borderRadius: 3,
              },
            }}
          />
        </Box>
      )}
    </Paper>
  );
}

export function GlobalStatsRow({ stats, loading = false, activeFilter, onStatClick, sx }: GlobalStatsRowProps) {
  const theme = useTheme();

  if (loading) {
    return (
      <Box sx={{ mb: 3, ...sx }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            gap: 2,
          }}
        >
          {[0, 1, 2, 3].map((index) => (
            <Paper
              key={index}
              elevation={0}
              sx={{ p: 2, borderRadius: 2, border: getCardBorder(theme) }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Skeleton animation="wave" variant="rounded" width={40} height={40} sx={{ mr: 1.5 }} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton animation="wave" variant="text" width={80} height={16} />
                  <Skeleton animation="wave" variant="text" width={60} height={32} />
                </Box>
              </Box>
            </Paper>
          ))}
        </Box>
      </Box>
    );
  }

  const onlinePercentage = stats.totalDevices > 0 ? (stats.upDevices / stats.totalDevices) * 100 : 0;
  const offlinePercentage = stats.totalDevices > 0 ? (stats.downDevices / stats.totalDevices) * 100 : 0;

  const handleClick = (filter: StatFilter) => {
    if (!onStatClick) return;
    // Toggle: clicking the active filter clears it back to 'all'
    onStatClick(activeFilter === filter ? 'all' : filter);
  };

  return (
    <Box sx={{ mb: 3, ...sx }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: 2,
        }}
      >
        <StatCard
          title="Total Devices"
          value={stats.totalDevices}
          icon={<DevicesIcon />}
          color={theme.palette.primary.main}
          isActive={activeFilter === 'all'}
          onClick={() => handleClick('all')}
        />
        <StatCard
          title="Online"
          value={stats.upDevices}
          icon={<OnlineIcon />}
          color={theme.palette.success.main}
          percentage={onlinePercentage}
          isActive={activeFilter === 'up'}
          onClick={() => handleClick('up')}
        />
        <StatCard
          title="Offline"
          value={stats.downDevices}
          icon={<OfflineIcon />}
          color={theme.palette.error.main}
          percentage={offlinePercentage}
          isActive={activeFilter === 'down'}
          onClick={() => handleClick('down')}
        />
        <StatCard
          title="Active Alerts"
          value={stats.totalAlerts}
          icon={<AlertIcon />}
          color={stats.totalAlerts > 0 ? theme.palette.error.main : theme.palette.warning.main}
          isActive={activeFilter === 'alerts'}
          onClick={() => handleClick('alerts')}
        />
      </Box>
    </Box>
  );
}

export default GlobalStatsRow;
